/**
 * TintsLab — A side-by-side comparison of `.paper` tint variants.
 *
 * Each cell renders the same sample composition (a card + a hand-note
 * + a chip) against a different `.paper--<tint>` modifier, so the
 * page's color story can be evaluated in context rather than as an
 * abstract swatch. Reachable at `/?tints` during design iteration.
 */
import { Frog, HeartStatImg, StrengthStatImg, ELEMENT_INFO } from '../design-system'
import { TINTS } from './paperTint'
import './TintsLab.css'

export function TintsLab() {
  return (
    <div className="tints-lab">
      <header className="tints-lab__head">
        <h1 className="cute-title">Paper Tint Atelier</h1>
        <p className="hand-note">
          Six bokashi color stories painted on the same washi photograph. Each
          cell uses the identical gradient geometry — only the dye palette
          shifts. Pick a favorite and we'll lock it in as the default, or wire
          a variant as a context-specific opt-in (e.g. <code>.paper--moonlit</code>
          on a night-battle screen).
        </p>
      </header>

      <div className="tints-lab__grid">
        {TINTS.map((tint) => (
          <TintCell key={tint.id || 'default'} tint={tint} />
        ))}
      </div>
    </div>
  )
}

function TintCell({ tint }: { tint: typeof TINTS[number] }) {
  const className = `paper paper--${tint.id} tints-cell`
  const fire = ELEMENT_INFO.fire
  return (
    <div className={className}>
      <div className="tints-cell__label">
        <span className="tiny-cap">{tint.id}</span>
        <h2 className="cute-title">{tint.name}</h2>
        <p className="hand-note">{tint.subtitle}</p>
      </div>

      <div className="tints-cell__sample">
        <div className="cute-card tints-card">
          <div className="tints-card__glyph">
            <Frog size={64} />
          </div>
          <div className="tints-card__body">
            <div className="tints-card__title">Reed Frog</div>
            <div className="tints-card__row">
              <span className="chip" style={{ background: fire.bg, color: fire.fg }}>
                {fire.kanji} {fire.en}
              </span>
              <HeartStatImg value={18} size={32} />
              <StrengthStatImg value={4} size={32} />
            </div>
            <p className="hand-note tints-card__note">
              Croaks ablaze · ignites the lily mat
            </p>
          </div>
        </div>

        <div className="tints-cell__caption hand-note">
          The quick brown fox jumps over a lazy paper crane.
        </div>
      </div>
    </div>
  )
}
